import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { ThemeProvider } from './context/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ModuleProtectedRoute } from './components/ModuleProtectedRoute';
import { CommandPalette } from './components/CommandPalette';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
      <p className="text-sm text-slate-500 dark:text-slate-400">Loading...</p>
    </div>
  </div>
);

// Critical pages - loaded immediately
import { LoginPage } from './pages/LoginPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { DashboardPage } from './pages/DashboardPage';

// Lazy loaded pages - code split for better initial load
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const ActivateAccountPage = lazy(() => import('./pages/ActivateAccountPage').then(m => ({ default: m.ActivateAccountPage })));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage').then(m => ({ default: m.ChangePasswordPage })));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));

// Incidents Module
const IncidentsPage = lazy(() => import('./pages/IncidentsPage').then(m => ({ default: m.IncidentsPage })));
const IncidentCreatePage = lazy(() => import('./pages/IncidentCreatePage').then(m => ({ default: m.IncidentCreatePage })));
const IncidentViewPage = lazy(() => import('./pages/IncidentViewPage').then(m => ({ default: m.IncidentViewPage })));

// Changes Module
const ChangesPage = lazy(() => import('./pages/ChangesPage').then(m => ({ default: m.ChangesPage })));
const ChangeCreatePage = lazy(() => import('./pages/ChangeCreatePage').then(m => ({ default: m.ChangeCreatePage })));
const ChangeViewPage = lazy(() => import('./pages/ChangeViewPage').then(m => ({ default: m.ChangeViewPage })));
const ChangeTemplateBuilderPage = lazy(() => import('./pages/ChangeTemplateBuilderPage').then(m => ({ default: m.ChangeTemplateBuilderPage })));
const CABConsolePage = lazy(() => import('./pages/CABConsolePage').then(m => ({ default: m.CABConsolePage })));

// Problems Module
const ProblemsPage = lazy(() => import('./pages/ProblemsPage').then(m => ({ default: m.ProblemsPage })));
const ProblemViewPage = lazy(() => import('./pages/ProblemViewPage'));
const ProblemCreatePage = lazy(() => import('./pages/ProblemCreatePage'));
const ProblemDashboardPage = lazy(() => import('./pages/ProblemDashboardPage'));

// Knowledge Module
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage').then(m => ({ default: m.KnowledgeBasePage })));
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage').then(m => ({ default: m.HelpCenterPage })));
const KnowledgeArticleViewPage = lazy(() => import('./pages/KnowledgeArticleViewPage').then(m => ({ default: m.KnowledgeArticleViewPage })));

// Survey Pages
const SurveyPage = lazy(() => import('./pages/SurveyPage').then(m => ({ default: m.SurveyPage })));
const SurveyAnalyticsPage = lazy(() => import('./pages/SurveyAnalyticsPage').then(m => ({ default: m.SurveyAnalyticsPage })));
const NPSSurveyPage = lazy(() => import('./pages/NPSSurveyPage').then(m => ({ default: m.NPSSurveyPage })));
const NPSSurveyManagementPage = lazy(() => import('./pages/NPSSurveyManagementPage').then(m => ({ default: m.NPSSurveyManagementPage })));
const NPSDashboardPage = lazy(() => import('./pages/NPSDashboardPage').then(m => ({ default: m.NPSDashboardPage })));

// Resolution Pages
const ResolutionConfirmPage = lazy(() => import('./pages/ResolutionConfirmPage'));
const ResolutionReopenedPage = lazy(() => import('./pages/ResolutionReopenedPage'));
const ResolutionClosedPage = lazy(() => import('./pages/ResolutionClosedPage'));

// Asset Management
const AssetsPage = lazy(() => import('./pages/AssetsPage').then(m => ({ default: m.AssetsPage })));

// Queue & Tasks
const QueuePage = lazy(() => import('./pages/QueuePage').then(m => ({ default: m.QueuePage })));
const TaskQueuePage = lazy(() => import('./pages/TaskQueuePage').then(m => ({ default: m.TaskQueuePage })));
const TaskViewPage = lazy(() => import('./pages/TaskViewPage'));

// User Management
const UsersPage = lazy(() => import('./pages/UsersPage').then(m => ({ default: m.UsersPage })));

// Reports
const ReportsPage = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const ReportPreviewPage = lazy(() => import('./pages/reports/ReportPreviewPage'));

// Configuration Pages
const AssignmentGroupsPage = lazy(() => import('./pages/AssignmentGroupsPage').then(m => ({ default: m.AssignmentGroupsPage })));
const AutoRoutingPage = lazy(() => import('./pages/AutoRoutingPage'));
const SLAConfigPage = lazy(() => import('./pages/SLAConfigPage').then(m => ({ default: m.SLAConfigPage })));
const SLAConfigurationPage = lazy(() => import('./pages/SLAConfigurationPage').then(m => ({ default: m.SLAConfigurationPage })));
const CategoriesPage = lazy(() => import('./pages/CategoriesPage').then(m => ({ default: m.CategoriesPage })));

// Service Catalog & Workflows
const ServiceCatalogPage = lazy(() => import('./pages/ServiceCatalogPage').then(m => ({ default: m.ServiceCatalogPage })));
const TaskFlowDesignerPage = lazy(() => import('./pages/TaskFlowDesignerPage').then(m => ({ default: m.TaskFlowDesignerPage })));
const WorkflowTemplatesPage = lazy(() => import('./pages/WorkflowTemplatesPage').then(m => ({ default: m.WorkflowTemplatesPage })));
const WorkflowTemplateBuilderPage = lazy(() => import('./pages/WorkflowTemplateBuilderPage').then(m => ({ default: m.WorkflowTemplateBuilderPage })));
const ServiceRequestsQueuePage = lazy(() => import('./pages/ServiceRequestsQueuePage').then(m => ({ default: m.ServiceRequestsQueuePage })));
const RequestCatalogPage = lazy(() => import('./pages/RequestCatalogPage').then(m => ({ default: m.RequestCatalogPage })));
const MyRequestsPage = lazy(() => import('./pages/MyRequestsPage').then(m => ({ default: m.MyRequestsPage })));

// Support & Chat
const SupportChatPage = lazy(() => import('./pages/SupportChatPage').then(m => ({ default: m.SupportChatPage })));
const LiveChatPage = lazy(() => import('./pages/LiveChatPage'));
const ChatHistoryPage = lazy(() => import('./pages/ChatHistoryPage'));
const CallCenterPage = lazy(() => import('./pages/CallCenterPage'));
const RequestCallbackPage = lazy(() => import('./pages/RequestCallbackPage'));
const AmieLearningPage = lazy(() => import('./pages/AmieLearningPage'));

// Work Management Pages
const AgentWorkConsolePage = lazy(() => import('./pages/AgentWorkConsolePage'));
const SupervisorDashboardPage = lazy(() => import('./pages/SupervisorDashboardPage'));

// Admin Pages
const ModuleSubscriptionPage = lazy(() => import('./pages/ModuleSubscriptionPage').then(m => ({ default: m.ModuleSubscriptionPage })));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage').then(m => ({ default: m.IntegrationsPage })));
const DeletedItemsPage = lazy(() => import('./pages/DeletedItemsPage').then(m => ({ default: m.DeletedItemsPage })));
const UPINRecordsPage = lazy(() => import('./pages/UPINRecordsPage').then(m => ({ default: m.UPINRecordsPage })));
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage').then(m => ({ default: m.AuditLogsPage })));
const SystemHealthPage = lazy(() => import('./pages/SystemHealthPage'));

// Settings
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage').then(m => ({ default: m.NotificationSettingsPage })));

// Announcements
const AnnouncementsAdminPage = lazy(() => import('./pages/AnnouncementsAdminPage').then(m => ({ default: m.AnnouncementsAdminPage })));
const AcknowledgeAnnouncementPage = lazy(() => import('./pages/AcknowledgeAnnouncementPage').then(m => ({ default: m.AcknowledgeAnnouncementPage })));

// Booking & Reminders
const BookingPage = lazy(() => import('./pages/BookingPage'));
const ReminderManagementPage = lazy(() => import('./pages/ReminderManagementPage'));

// Handover
const ConfirmHandoverPage = lazy(() => import('./pages/ConfirmHandoverPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const WidgetSettingsPage = lazy(() => import('./pages/WidgetSettingsPage'));

// Router component that handles Google SSO callback detection
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions by processing new session_id FIRST
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/activate/:token" element={<ActivateAccountPage />} />
        <Route path="/survey/:token" element={<SurveyPage />} />
        <Route path="/nps-survey/:token" element={<NPSSurveyPage />} />
        <Route path="/resolution/:token/reopen" element={<ResolutionConfirmPage />} />
        <Route path="/resolution/:token/reopened" element={<ResolutionReopenedPage />} />
        <Route path="/resolution/:token/closed" element={<ResolutionClosedPage />} />
        <Route path="/acknowledge-announcement" element={<AcknowledgeAnnouncementPage />} />
        <Route path="/booking/:token" element={<BookingPage />} />
        <Route path="/confirm-handover/:token" element={<ConfirmHandoverPage />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePasswordPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/incidents"
          element={
            <ProtectedRoute>
              <ModuleProtectedRoute>
                <IncidentsPage />
              </ModuleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/incidents/new"
          element={
            <ProtectedRoute>
              <ModuleProtectedRoute>
                <IncidentCreatePage />
              </ModuleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/incidents/:incidentId"
          element={
            <ProtectedRoute>
              <ModuleProtectedRoute>
                <IncidentViewPage />
              </ModuleProtectedRoute>
            </ProtectedRoute>
          }
        />
        {/* Redirect old /requests to /request-catalog */}
        <Route path="/requests" element={<Navigate to="/request-catalog" replace />} />
        <Route
          path="/changes"
          element={
            <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin']}>
              <ModuleProtectedRoute>
                <ChangesPage />
              </ModuleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/changes/new"
          element={
            <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin']}>
              <ModuleProtectedRoute>
                <ChangeCreatePage />
              </ModuleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/changes/:changeId"
          element={
            <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin']}>
              <ModuleProtectedRoute>
                <ChangeViewPage />
              </ModuleProtectedRoute>
            </ProtectedRoute>
          }
      />
      <Route
        path="/changes/templates/new"
        element={
          <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <ChangeTemplateBuilderPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/changes/templates/:templateId/edit"
        element={
          <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <ChangeTemplateBuilderPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cab-console"
        element={
          <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <CABConsolePage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/problems"
        element={
          <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <ProblemsPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/problems/create"
        element={
          <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <ProblemCreatePage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/problems/:id"
        element={
          <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <ProblemViewPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/problems/dashboard"
        element={
          <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <ProblemDashboardPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-management"
        element={
          <ProtectedRoute>
            <ModuleProtectedRoute>
              <KnowledgeBasePage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      {/* Redirect old knowledge routes to new consolidated route */}
      <Route path="/knowledge" element={<Navigate to="/knowledge-management" replace />} />
      <Route path="/knowledge-base" element={<Navigate to="/knowledge-management" replace />} />
      
      {/* Knowledge Article Detail View - Opens in new tab */}
      <Route 
        path="/knowledge-article/:articleId" 
        element={
          <ProtectedRoute>
            <KnowledgeArticleViewPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/help-center"
        element={
          <ProtectedRoute>
            <ModuleProtectedRoute>
              <HelpCenterPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'itil_staff', 'end_user']}>
            <ModuleProtectedRoute>
              <AssetsPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/queue"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'itil_staff']}>
            <ModuleProtectedRoute>
              <QueuePage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <UsersPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'itil_staff']}>
            <ModuleProtectedRoute>
              <ReportsPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/preview"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'itil_staff']}>
            <ReportPreviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/survey-analytics"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'itil_staff']}>
            <ModuleProtectedRoute>
              <SurveyAnalyticsPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/nps-surveys"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <NPSSurveyManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/nps-dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'itil_staff']}>
            <NPSDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/auto-routing"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <AutoRoutingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assignment-groups"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'itil_staff']}>
            <ModuleProtectedRoute>
              <AssignmentGroupsPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sla-config"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <SLAConfigPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sla-configuration"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <SLAConfigurationPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-catalog"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <ServiceCatalogPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/task-flow-designer"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <TaskFlowDesignerPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflow-templates"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <WorkflowTemplatesPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflow-template-builder"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <ModuleProtectedRoute>
              <WorkflowTemplateBuilderPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reminder-management"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'itil_admin']}>
            <ReminderManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-requests-queue"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'itil_staff']}>
            <ModuleProtectedRoute>
              <ServiceRequestsQueuePage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/task-queue"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'itil_staff']}>
            <ModuleProtectedRoute>
              <TaskQueuePage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks/:taskId"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'itil_staff']}>
            <TaskViewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/request-catalog"
        element={
          <ProtectedRoute>
            <ModuleProtectedRoute>
              <RequestCatalogPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-requests"
        element={
          <ProtectedRoute>
            <ModuleProtectedRoute>
              <MyRequestsPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support-chat"
        element={
          <ProtectedRoute>
            <ModuleProtectedRoute>
              <SupportChatPage />
            </ModuleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/live-chat"
        element={
          <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin']}>
            <LiveChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat-history"
        element={
          <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin']}>
            <ChatHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/call-center"
        element={
          <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin']}>
            <CallCenterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/request-callback"
        element={
          <ProtectedRoute>
            <RequestCallbackPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/amie-learning"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <AmieLearningPage />
          </ProtectedRoute>
        }
      />
      
      {/* Work Management Routes */}
      <Route
        path="/work-console"
        element={
          <ProtectedRoute allowedRoles={['itil_staff', 'admin', 'super_admin', 'supervisor']}>
            <AgentWorkConsolePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/supervisor-dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'supervisor']}>
            <SupervisorDashboardPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/module-subscriptions"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <ModuleSubscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <IntegrationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/categories"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <CategoriesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/upin-records"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <UPINRecordsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deleted-items"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <DeletedItemsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AuditLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/system-health"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SystemHealthPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/announcements"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'itil_staff']}>
            <AnnouncementsAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/widget-settings"
        element={
          <ProtectedRoute>
            <WidgetSettingsPage />
          </ProtectedRoute>
        }
      />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <SubscriptionProvider>
          <BrowserRouter>
            <AppRouter />
            <CommandPalette />
            <Toaster />
          </BrowserRouter>
        </SubscriptionProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
